import type { DataResponseBody, PageResponse, FileResponse } from "./common.types";
import type { SiteRegion } from "./sites.types";

export type KnownEventCategory = "DETECTION" | "SAFETY";

export type EventCategory = KnownEventCategory | (string & {});

export const CCTV_EVENT_CATEGORIES: KnownEventCategory[] = ["DETECTION"];

export function isCctvEventCategory(category: EventCategory | undefined | null): boolean {
  if (category == null) return true;
  return (CCTV_EVENT_CATEGORIES as string[]).includes(category);
}

export type KnownEventType =
  | "NO_HELMET"
  | "FIRE"
  | "INTRUSION"
  | "GAS_THRESHOLD_EXCEEDED"
  | "BAND_VITAL_ABNORMAL"
  | "HEAT_INDEX_EXCEEDED";

export type EventType = KnownEventType | (string & {});

export type EventSeverity =
  | "NORMAL"
  | "UNCERTAIN"
  | "ATTENTION"
  | "CAUTION"
  | "WARNING"
  | "CRITICAL";

/**
 * 이벤트가 어디서 왔나. `KMA`(기상청)는 safers-api#200 으로 발행이 중단돼 스펙에서
 * 빠졌다 — 체감온도 판정은 기상청이 아니라 온습도계 측정값으로만 한다.
 */
export type EventSource = "GAS_SENSOR" | "SMART_BAND" | "THERMO_SENSOR";

export interface EventSite {
  id: number;
  name: string;
  region: SiteRegion;
}

export interface Event {
  id: number;
  eventId: string;
  timestamp: string;
  category: EventCategory;
  type: EventType;
  trackId: number;
  name: string;
  confidence: number;
  path: string;
  site: EventSite;
  snapshot?: FileResponse;
  video?: FileResponse;
  severity?: EventSeverity;
  source?: EventSource;
  deviceId?: string;
  bandId?: string;
  lat?: number;
  lon?: number;
  alt?: number;
  accuracyM?: number;
  payload?: Record<string, unknown>;
}

export type EventsResponse = DataResponseBody<PageResponse<Event>>;
export type EventDetailResponse = DataResponseBody<Event>;

export interface GetEventsParams {
  page?: number;
  size?: number;
  siteId?: number;
  query?: string;

  category?: EventCategory | EventCategory[];
}

export interface EventTypeInfo {
  name: EventType;
  displayName: string;
}

export interface EventCategoryInfo {
  name: EventCategory;
  displayName: string;
  types: EventTypeInfo[];
}

export type EventCategoriesResponse = DataResponseBody<EventCategoryInfo[]>;

export type EventSeverityBucket =
  | "critical"
  | "warning"
  | "caution"
  | "attention"
  | "uncertain"
  | "normal";

const SEVERITY_BUCKET: Record<EventSeverity, EventSeverityBucket> = {
  CRITICAL: "critical",
  WARNING: "warning",
  CAUTION: "caution",
  ATTENTION: "attention",
  UNCERTAIN: "uncertain",
  NORMAL: "normal",
};

const SEVERITY_FALLBACK: EventSeverityBucket = "attention";

// `Pick<Event,"severity">` 로 좁히지 말 것 — REST `Event`(?: EventSeverity)와 실시간
// `EventMessage`(?: EventSeverity | null)가 같은 표에 섞인다.
export function eventSeverity(event: { severity?: EventSeverity | null }): EventSeverityBucket {
  if (!event.severity) return SEVERITY_FALLBACK;
  return SEVERITY_BUCKET[event.severity] ?? SEVERITY_FALLBACK;
}

export function isUncertainEvent(event: { severity?: EventSeverity | null }): boolean {
  return eventSeverity(event) === "uncertain";
}

// severity-* 는 @safers/ui globals.css 의 @theme 토큰에서 Tailwind 가 만드는 유틸리티다.
// 램프를 직접 지목(bg-yellow-200)하지 말 것 — 팔레트를 고쳐도 따라오지 않는다.
export const EVENT_SEVERITY_STYLES = {
  critical: { bg: "bg-severity-danger-200", text: "text-severity-danger-800", label: "위험" },
  warning: { bg: "bg-severity-warning-200", text: "text-severity-warning-800", label: "경고" },
  caution: { bg: "bg-severity-caution-200", text: "text-severity-caution-800", label: "주의" },
  attention: {
    bg: "bg-severity-attention-200",
    text: "text-severity-attention-800",
    label: "관심",
  },
  uncertain: {
    bg: "bg-severity-uncertain-200",
    text: "text-severity-uncertain-800",
    label: "불확실",
  },
  normal: { bg: "bg-severity-normal-200", text: "text-severity-normal-800", label: "정상" },
} as const;

export function severityChip(severity: EventSeverity | null | undefined) {
  return EVENT_SEVERITY_STYLES[eventSeverity({ severity: severity ?? undefined })];
}

export const EVENT_REGION_MAP: Record<SiteRegion, string> = {
  SEOUL: "서울",
  GYEONGGI_INCHEON: "경기",
  GANGWON: "강원",
  CHUNGCHEONG: "충청",
  JEOLLA: "전라",
  GYEONGSANG: "경상",
  JEJU: "제주",
};

export const EVENT_REGIONS = ["전체", ...Object.values(EVENT_REGION_MAP)] as const;
export type EventRegionTab = (typeof EVENT_REGIONS)[number];

export const EVENT_REGION_LABEL_TO_KEY: Record<string, SiteRegion> = Object.fromEntries(
  Object.entries(EVENT_REGION_MAP).map(([key, label]) => [label, key as SiteRegion])
);

export type StompEventType = EventType;
export type StompEventCategory = EventCategory;

// `uuid`·`region` 이 선택인 이유 — cctv-ai 지면이 REST `/events` 목록과 실시간 메시지를
// 한 배열에 섞어 쓰는데 REST 쪽 `site` 에는 `uuid` 가 없다.
export interface EventSiteInfo {
  id: number;
  name: string;
  region?: SiteRegion | null;
  uuid?: string;
}

// payload 를 화면에 뿌리기 위한 표시 정보. payload 가 없으면 빈 목록.
export interface EventMetadataField {
  key: string;
  label: string;
  type: string;
  unit?: string | null;
}

// `/user/queue/events` 페이로드. `category` 는 DETECTION|SAFETY 를 가르는 유일한 기준이라
// 없으면 권한 필터와 cctv-ai 목록이 둘 다 어긋난다.
// `severity` 는 AsyncAPI 스펙상 nullable 이지만 실제로는 항상 채워져 온다(서버 확인).
export interface EventMessage {
  id: number;
  eventId: string;
  name: string;
  type: EventType;
  category: EventCategory;
  timestamp: string;
  severity?: EventSeverity | null;
  site: EventSiteInfo;
  path?: string | null;
  trackId?: number | null;
  confidence?: number | null;
  snapshot?: FileResponse;
  video?: FileResponse;
  source?: EventSource | null;
  deviceId?: string | null;
  lat?: number | null;
  lon?: number | null;
  alt?: number | null;
  payload?: Record<string, unknown>;
  metadata?: EventMetadataField[];
}

// @deprecated `EventMessage` 를 쓸 것. cctv-ai 지면이 아직 이 이름을 쓴다.
export type StompEventResponse = EventMessage;

export interface SiteEmergencyPayload {
  workerId: string;
  position: { lng: number; lat: number; altitude: number };
  vitals: { temperature: number; heartRate: number };
}

export interface SiteEvent extends Event {
  emergency?: SiteEmergencyPayload;
}
